// src/pages/AdminJournalEditor.jsx

import React, { useEffect, useState } from "react";
import {
    Link,
    useNavigate,
    useParams,
} from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import AdminHeader from "../components/Admin/AdminHeader";
import RichTextEditor from "../components/Admin/RichTextEditor";
import "./adminJournalEditor.css";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB

const AdminJournalEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const isEditMode = Boolean(id);

    const [form, setForm] = useState({
        title: "",
        slug: "",
        category: "",
        excerpt: "",
        content: "",
        cover_image: "",
        published: false,
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [originalCoverImage, setOriginalCoverImage] = useState("");

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    /* =========================================
       LOAD ARTICLE
       ========================================= */

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }

        const loadArticle = async () => {
            setLoading(true);
            setError("");

            const { data, error } = await supabase
                .from("journal_articles")
                .select(`
          id,
          title,
          slug,
          category,
          excerpt,
          content,
          cover_image,
          published,
          published_at
        `)
                .eq("id", id)
                .single();

            if (error) {
                console.error("Error loading article:", error);
                setError("Unable to load this article.");
                setLoading(false);
                return;
            }

            setForm({
                title: data.title || "",
                slug: data.slug || "",
                category: data.category || "",
                excerpt: data.excerpt || "",
                content: data.content || "",
                cover_image: data.cover_image || "",
                published: Boolean(data.published),
            });

            if (data.cover_image) {
                setImagePreview(data.cover_image);
                setOriginalCoverImage(data.cover_image);
            }

            setLoading(false);
        };

        loadArticle();
    }, [id, isEditMode]);

    /* =========================================
       CLEANUP IMAGE PREVIEW
       ========================================= */

    useEffect(() => {
        return () => {
            if (
                imagePreview &&
                imagePreview.startsWith("blob:")
            ) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    /* =========================================
       HANDLE INPUT
       ========================================= */

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: type === "checkbox" ? checked : value,
        }));

        setError("");
        setSuccess("");
    };

    /* =========================================
       GENERATE SLUG
       ========================================= */

    const generateSlug = () => {
        const slug = form.title
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-");

        setForm((previous) => ({
            ...previous,
            slug,
        }));
    };

    /* =========================================
       IMAGE SELECTION
       ========================================= */

    const handleImageSelect = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        setError("");
        setSuccess("");

        /* Validate type */

        if (!file.type.startsWith("image/")) {
            setError("Please select a valid image file.");
            event.target.value = "";
            return;
        }

        /* Validate size */

        if (file.size > MAX_IMAGE_SIZE) {
            setError("Image size must be less than 5 MB.");
            event.target.value = "";
            return;
        }

        /* Remove old blob preview */

        if (
            imagePreview &&
            imagePreview.startsWith("blob:")
        ) {
            URL.revokeObjectURL(imagePreview);
        }

        setSelectedImage(file);
        setImagePreview(URL.createObjectURL(file));

        /*
          Don't immediately change form.cover_image.
          The actual Supabase URL will be created
          only after the article is saved.
        */
    };

    /* =========================================
       REMOVE IMAGE
       ========================================= */

    const handleRemoveImage = () => {
        if (
            imagePreview &&
            imagePreview.startsWith("blob:")
        ) {
            URL.revokeObjectURL(imagePreview);
        }

        setSelectedImage(null);
        setImagePreview("");
        setForm((previous) => ({
            ...previous,
            cover_image: "",
        }));

        const input = document.getElementById(
            "journal-cover-image"
        );

        if (input) {
            input.value = "";
        }
    };

    /* =========================================
       VALIDATION
       ========================================= */

    const validateForm = () => {
        if (!form.title.trim()) {
            return "Please enter an article title.";
        }

        if (!form.slug.trim()) {
            return "Please enter a slug.";
        }

        if (!form.category.trim()) {
            return "Please enter a category.";
        }

        if (!form.excerpt.trim()) {
            return "Please enter an excerpt.";
        }

        if (!form.content.trim()) {
            return "Please enter the article content.";
        }

        return null;
    };

    /* =========================================
       GET STORAGE FILE PATH
       ========================================= */

    const getStoragePathFromUrl = (url) => {
        if (!url) {
            return null;
        }

        const marker =
            "/storage/v1/object/public/journal-images/";

        const index = url.indexOf(marker);

        if (index === -1) {
            return null;
        }

        return decodeURIComponent(
            url.substring(index + marker.length)
        );
    };

    /* =========================================
       UPLOAD IMAGE
       ========================================= */

    const uploadImage = async (file) => {
        const fileExtension =
            file.name.split(".").pop()?.toLowerCase() || "jpg";

        const safeTitle = form.title
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .substring(0, 60);

        const uniqueName =
            `${Date.now()}-${Math.random()
                .toString(36)
                .substring(2, 8)}`;

        const filePath =
            `${safeTitle || "journal-article"}/${uniqueName}.${fileExtension}`;

        const { error: uploadError } = await supabase.storage
            .from("journal-images")
            .upload(filePath, file, {
                cacheControl: "3600",
                upsert: false,
                contentType: file.type,
            });

        if (uploadError) {
            console.error(
                "Error uploading journal image:",
                uploadError
            );

            throw new Error(
                uploadError.message ||
                "Unable to upload the cover image."
            );
        }

        const { data } = supabase.storage
            .from("journal-images")
            .getPublicUrl(filePath);

        if (!data?.publicUrl) {
            throw new Error(
                "Image uploaded, but a public URL could not be generated."
            );
        }

        return {
            publicUrl: data.publicUrl,
            filePath,
        };
    };

    /* =========================================
       DELETE STORAGE IMAGE
       ========================================= */

    const deleteStorageImage = async (url) => {
        const filePath = getStoragePathFromUrl(url);


        if (!filePath) {
            console.error("Could not extract Supabase storage path.");
            return false;
        }

        const { data, error } = await supabase.storage
            .from("journal-images")
            .remove([filePath]);

        console.log("Supabase delete data:", data);
        console.log("Supabase delete error:", error);

        if (error) {
            console.error("Storage deletion failed:", error);
            return false;
        }

        if (!data || data.length === 0) {
            console.warn(
                "Supabase returned 200 but no storage object was deleted."
            );
            return false;
        }

        console.log("Image successfully deleted:", data);

        return true;
    };
    /* =========================================
       SAVE ARTICLE
       ========================================= */

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError("");
        setSuccess("");

        const validationError = validateForm();

        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);

        let uploadedImagePath = null;
        let newCoverImage = form.cover_image || "";

        try {
            /*
              1. Upload newly selected image
            */

            if (selectedImage) {
                const uploaded = await uploadImage(selectedImage);

                newCoverImage = uploaded.publicUrl;
                uploadedImagePath = uploaded.filePath;
            }

            /*
              2. Preserve the original publication date
              when editing an already published article.
            */

            let publishedAt = null;

            if (form.published) {
                if (isEditMode) {
                    const { data: existingArticle, error: existingError } =
                        await supabase
                            .from("journal_articles")
                            .select("published_at")
                            .eq("id", id)
                            .single();

                    if (existingError) {
                        throw existingError;
                    }

                    publishedAt =
                        existingArticle?.published_at ||
                        new Date().toISOString();
                } else {
                    publishedAt = new Date().toISOString();
                }
            }

            /*
              3. Prepare database data
            */

            const articleData = {
                title: form.title.trim(),
                slug: form.slug.trim(),
                category: form.category.trim(),
                excerpt: form.excerpt.trim(),
                content: form.content.trim(),
                cover_image: newCoverImage || null,
                published: form.published,
                published_at: publishedAt,
                updated_at: new Date().toISOString(),
            };

            /*
              4. CREATE
            */

            if (!isEditMode) {
                const { data, error } = await supabase
                    .from("journal_articles")
                    .insert(articleData)
                    .select();

                if (error) {
                    throw error;
                }

                if (!data || data.length === 0) {
                    throw new Error(
                        "Article could not be created. Check your permissions."
                    );
                }

                setSuccess("Article created successfully.");

                /*
                  Navigate to edit page after a short delay
                  so the success message can be seen.
                */

                setTimeout(() => {
                    navigate(`/admin/journal/edit/${data[0].id}`);
                }, 500);

                return;
            }

            /*
              5. UPDATE
            */

            const { data, error } = await supabase
                .from("journal_articles")
                .update(articleData)
                .eq("id", id)
                .select();

            if (error) {
                throw error;
            }

            if (!data || data.length === 0) {
                throw new Error(
                    "Article could not be updated. Check your permissions."
                );
            }

            /*
              6. If the old image was replaced or removed,
                 delete the old Supabase Storage file.
            
              originalCoverImage = image that existed when editing started
              newCoverImage      = image currently saved to the article
            */

            if (
                isEditMode &&
                originalCoverImage &&
                originalCoverImage !== newCoverImage
            ) {
                await deleteStorageImage(originalCoverImage);
            }

            setForm((previous) => ({
                ...previous,
                cover_image: newCoverImage,
            }));

            setOriginalCoverImage(newCoverImage);
            setSelectedImage(null);

            setSuccess("Article updated successfully.");

        } catch (err) {
            console.error("Error saving article:", err);

            /*
              If image upload succeeded but database save failed,
              remove the newly uploaded image so we don't leave
              an orphaned file in Storage.
            */

            if (uploadedImagePath) {
                const { error: cleanupError } =
                    await supabase.storage
                        .from("journal-images")
                        .remove([uploadedImagePath]);

                if (cleanupError) {
                    console.error(
                        "Unable to clean up uploaded image:",
                        cleanupError
                    );
                }
            }

            setError(
                err?.message ||
                "Unable to save the article. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };

    /* =========================================
       LOADING
       ========================================= */

    if (loading) {
        return (
            <div className="admin-page">
                <AdminHeader backTo="/admin/journal" backLabel="Journal" />

                <main className="admin-editor-page">
                    <div className="admin-editor-state">
                        <div className="admin-auth-spinner"></div>
                        <p>Loading article...</p>
                    </div>
                </main>
            </div>
        );
    }

    /* =========================================
       RENDER
       ========================================= */

    return (
        <div className="admin-page">

            <AdminHeader
                backTo="/admin/journal"
                backLabel="Journal"
            />

            <main className="admin-editor-page">

                <div className="admin-editor-container">

                    {/* PAGE HEADER */}

                    <div className="admin-editor-title">

                        <div>
                            <span className="admin-editor-eyebrow">
                                JOURNAL MANAGEMENT
                            </span>

                            <h1>
                                {isEditMode
                                    ? "Edit Article"
                                    : "Create New Article"}
                            </h1>

                            <p>
                                {isEditMode
                                    ? "Update the article content and publishing details."
                                    : "Create a new article for the Sudhamshu Ayurveda Journal."}
                            </p>
                        </div>

                    </div>


                    {/* ERROR */}

                    {error && (
                        <div className="admin-editor-message admin-editor-error">
                            {error}
                        </div>
                    )}


                    {/* SUCCESS */}

                    {success && (
                        <div className="admin-editor-message admin-editor-success">
                            {success}
                        </div>
                    )}


                    <form
                        className="admin-journal-editor"
                        onSubmit={handleSubmit}
                    >

                        {/* BASIC INFORMATION */}

                        <section className="admin-editor-card">

                            <div className="admin-editor-card-heading">
                                <h2>Article Information</h2>
                                <p>
                                    Basic information about the journal article.
                                </p>
                            </div>


                            {/* TITLE */}

                            <div className="admin-editor-form-group">

                                <label htmlFor="title">
                                    Title
                                </label>

                                <input
                                    id="title"
                                    name="title"
                                    type="text"
                                    value={form.title}
                                    onChange={handleChange}
                                    placeholder="Enter article title"
                                />

                            </div>


                            {/* SLUG */}

                            <div className="admin-editor-form-group">

                                <label htmlFor="slug">
                                    Slug
                                </label>

                                <div className="admin-editor-slug-row">

                                    <input
                                        id="slug"
                                        name="slug"
                                        type="text"
                                        value={form.slug}
                                        onChange={handleChange}
                                        placeholder="article-url-slug"
                                    />

                                    <button
                                        type="button"
                                        className="admin-editor-secondary-btn"
                                        onClick={generateSlug}
                                    >
                                        Generate
                                    </button>

                                </div>

                                <small>
                                    Used in the public article URL.
                                </small>

                            </div>


                            {/* CATEGORY */}

                            <div className="admin-editor-form-group">

                                <label htmlFor="category">
                                    Category
                                </label>

                                <input
                                    id="category"
                                    name="category"
                                    type="text"
                                    value={form.category}
                                    onChange={handleChange}
                                    placeholder="Ayurveda"
                                />

                            </div>


                            {/* EXCERPT */}

                            <div className="admin-editor-form-group">

                                <label htmlFor="excerpt">
                                    Excerpt
                                </label>

                                <textarea
                                    id="excerpt"
                                    name="excerpt"
                                    rows="4"
                                    value={form.excerpt}
                                    onChange={handleChange}
                                    placeholder="Short description of the article..."
                                />

                                <small>
                                    This appears on the Journal listing and homepage.
                                </small>

                            </div>

                        </section>


                        {/* COVER IMAGE */}

                        <section className="admin-editor-card">

                            <div className="admin-editor-card-heading">
                                <h2>Cover Image</h2>

                                <p>
                                    Upload an image to use as the article cover.
                                </p>
                            </div>


                            <div className="admin-journal-image-upload">

                                {/* PREVIEW */}

                                {imagePreview ? (

                                    <div className="admin-journal-image-preview">

                                        <img
                                            src={imagePreview}
                                            alt="Article cover preview"
                                        />

                                        <button
                                            type="button"
                                            className="admin-journal-image-remove"
                                            onClick={handleRemoveImage}
                                        >
                                            Remove Image
                                        </button>

                                    </div>

                                ) : (

                                    <label
                                        htmlFor="journal-cover-image"
                                        className="admin-journal-image-dropzone"
                                    >

                                        <div className="admin-journal-upload-icon">
                                            ↑
                                        </div>

                                        <strong>
                                            Choose a cover image
                                        </strong>

                                        <span>
                                            JPG, PNG, WEBP · Maximum 5 MB
                                        </span>

                                    </label>

                                )}


                                {/* FILE INPUT */}

                                <input
                                    id="journal-cover-image"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageSelect}
                                    className="admin-journal-file-input"
                                />


                                {/* CHANGE IMAGE */}

                                {imagePreview && (
                                    <label
                                        htmlFor="journal-cover-image"
                                        className="admin-editor-secondary-btn admin-journal-change-image"
                                    >
                                        Choose Different Image
                                    </label>
                                )}

                            </div>

                        </section>


                        {/* CONTENT */}

                        <section className="admin-editor-card">

                            <div className="admin-editor-card-heading">
                                <h2>Article Content</h2>

                                <p>
                                    Write the main content of the article.
                                </p>
                            </div>


                            <div className="admin-editor-form-group">

                                <label htmlFor="content">
                                    Content
                                </label>

                                {/* <textarea
                                    id="content"
                                    name="content"
                                    rows="18"
                                    value={form.content}
                                    onChange={handleChange}
                                    placeholder="Write your article here..."
                                    className="admin-journal-content-textarea"
                                /> */}
                                <RichTextEditor
                                    value={form.content}
                                    onChange={(html) => {
                                        setForm((previous) => ({
                                            ...previous,
                                            content: html,
                                        }));
                                        setError("");
                                        setSuccess("");
                                    }}
                                />

                                <small>
                                    Separate paragraphs with an empty line.
                                </small>

                            </div>

                        </section>


                        {/* PUBLISHING */}

                        <section className="admin-editor-card">

                            <div className="admin-editor-card-heading">
                                <h2>Publishing</h2>

                                <p>
                                    Control whether this article is visible
                                    on the public website.
                                </p>
                            </div>


                            <label className="admin-publish-toggle">

                                <input
                                    type="checkbox"
                                    name="published"
                                    checked={form.published}
                                    onChange={handleChange}
                                />

                                <span className="admin-publish-switch"></span>

                                <span className="admin-publish-label">

                                    <strong>
                                        {form.published
                                            ? "Published"
                                            : "Draft"}
                                    </strong>

                                    <small>
                                        {form.published
                                            ? "This article is visible to website visitors."
                                            : "This article is only visible in the admin panel."}
                                    </small>

                                </span>

                            </label>

                        </section>


                        {/* ACTIONS */}

                        <div className="admin-editor-actions">

                            <Link
                                to="/admin/journal"
                                className="admin-editor-cancel-btn"
                            >
                                Cancel
                            </Link>

                            <button
                                type="submit"
                                className="admin-editor-save-btn"
                                disabled={saving}
                            >
                                {saving
                                    ? "Saving..."
                                    : isEditMode
                                        ? "Save Changes"
                                        : "Create Article"}
                            </button>

                        </div>

                    </form>

                </div>

            </main>

        </div>
    );
};

export default AdminJournalEditor;